import { ServiceHandler, responseStringTransformer } from '../utils';
import { standardQuerySerializer } from './serializers';
import {
  UserSearchResult,
  SignatureOption,
  SignatureMapping,
} from '../types/signature-assignment.types';

// API Response Types
export interface UserSearchResponse {
  users: UserSearchResult[];
  totalCount: number;
}

export interface SignaturesByEmailResponse {
  signatures: SignatureOption[];
  userInfo: {
    email: string;
    firstName: string;
    lastName: string;
    type: 'user' | 'team';
  };
}

export interface ConfigureSignaturesResponse {
  success: boolean;
  message: string;
  updatedMappings: SignatureMapping[];
}

export interface SignatureAssignmentsResponse {
  assignments: SignatureMapping[];
  totalCount: number;
}

export interface DeleteSignatureAssignmentResponse {
  success: boolean;
  message: string;
}

// API Service Functions
export const searchUsers = ServiceHandler<
  { query: string; limit?: number },
  UserSearchResponse
>({
  url: '/api/docmessage/v1/system/signatures/search-users',
  method: 'GET',
  paramsSerializer: standardQuerySerializer,
  transformResponse: [responseStringTransformer],
});

export const getSignaturesByEmail = ServiceHandler<
  string,
  SignaturesByEmailResponse
>({
  url: '/api/docmessage/v1/system/signatures/by-email/:userEmail',
  method: 'GET',
  transformResponse: [responseStringTransformer],
});

/** The mailbox assigned to a message category. 404 (plain string) where none is assigned. */
export interface CategorySender {
  email: string;
  firstName: string;
  lastName: string;
  /** The display name the real From carries; null where the mailbox has none. */
  name: string | null;
}

export const getCategorySender = ServiceHandler<
  { category: string },
  CategorySender
>({
  url: '/api/docmessage/v1/system/signatures/sender',
  method: 'GET',
  transformResponse: [responseStringTransformer],
});

export const configureSignatures = ServiceHandler<
  SignatureMapping[],
  ConfigureSignaturesResponse
>({
  url: '/api/docmessage/v1/system/signatures/configure',
  method: 'POST',
  transformResponse: [responseStringTransformer],
});

export const getSignatureAssignments = ServiceHandler<
  Record<string, never>,
  SignatureAssignmentsResponse
>({
  url: '/api/docmessage/v1/system/signatures/assignments',
  method: 'GET',
  transformResponse: [responseStringTransformer],
});

export const deleteSignatureAssignment = ServiceHandler<
  { msgCategory: string },
  DeleteSignatureAssignmentResponse
>({
  url: '/api/docmessage/v1/system/signatures/assignments/:msgCategory',
  method: 'DELETE',
  transformResponse: [responseStringTransformer],
});

// Hook for existing signature configurations (if needed)
export const getCurrentSignatureConfiguration = ServiceHandler<
  Record<string, never>,
  SignatureMapping[]
>({
  url: '/api/docmessage/v1/system/signatures/current-configuration',
  method: 'GET',
  transformResponse: [responseStringTransformer],
});
